// Kartvy's "Visa bara bostäder i" search: kommuner, län, postorter and RegSO areas, each with the areas it
// covers. Area names come from the overlay topology (`regso`, `regsokod`), kommun names from
// public/kommunKoder.json, län from lanAreas.js (a län's areas are the RegSO ids starting with its code) and
// postorter from public/postortAreas.json.
import { LAN_LIST } from './lanAreas.js';

const TYPE_ORDER = { kommun: 0, lan: 1, postort: 2, area: 3 };

export const normalizeAreaSearch = (value) => String(value || '')
    .toLowerCase()
    .replace(/[\s,]+/g, ' ')
    .trim();

export const buildAreaSearchEntries = ({ topology, kommunNames = {}, postorter = [] }) => {
    const object = topology?.objects?.[Object.keys(topology.objects || {})[0]];
    const geometries = Array.isArray(object?.geometries) ? object.geometries : [];
    const regsoIdsByKommun = new Map();
    const entries = [];
    geometries.forEach((geometry) => {
        const regsoId = String(geometry?.properties?.regsokod || '').trim();
        const name = String(geometry?.properties?.regso || '').trim();
        if (!regsoId) return;
        const kommunCode = regsoId.slice(0, 4);
        if (!regsoIdsByKommun.has(kommunCode)) regsoIdsByKommun.set(kommunCode, []);
        regsoIdsByKommun.get(kommunCode).push(regsoId);
        if (name) {
            entries.push({
                id: `area:${regsoId}`,
                type: 'area',
                name,
                place: kommunNames[kommunCode] || '',
                regsoIds: [regsoId],
            });
        }
    });
    Object.entries(kommunNames).forEach(([code, name]) => {
        const regsoIds = regsoIdsByKommun.get(code);
        if (!regsoIds?.length || !name) return;
        entries.push({ id: `kommun:${code}`, type: 'kommun', name, place: '', regsoIds });
    });
    LAN_LIST.forEach((lan) => {
        const regsoIds = [];
        regsoIdsByKommun.forEach((ids, kommunCode) => {
            if (kommunCode.startsWith(lan.code)) regsoIds.push(...ids);
        });
        if (!regsoIds.length) return;
        entries.push({ id: `lan:${lan.code}`, type: 'lan', name: lan.label, place: '', regsoIds, lanCode: lan.code });
    });
    postorter.forEach((postort) => {
        const regsoIds = (Array.isArray(postort?.regsoIds) ? postort.regsoIds : []).filter(Boolean);
        if (!postort?.name || !regsoIds.length) return;
        entries.push({
            id: `postort:${normalizeAreaSearch(postort.name)}:${postort.kommun}`,
            type: 'postort',
            name: postort.name,
            place: postort.kommunName || kommunNames[postort.kommun] || '',
            regsoIds,
        });
    });
    return entries.map((entry) => ({ ...entry, key: normalizeAreaSearch(entry.name) }));
};

// The best matches: names starting with the query first, then a word starting with it, then containing it;
// kommuner before län before postorter before areas, shorter names first.
export const searchAreaEntries = (entries, query, limit = 6) => {
    const q = normalizeAreaSearch(query);
    if (q.length < 2) return [];
    const ranked = [];
    entries.forEach((entry) => {
        let rank = -1;
        if (entry.key.startsWith(q)) rank = 0;
        else if (entry.key.split(/[\s-]+/).some((word) => word.startsWith(q))) rank = 1;
        else if (entry.key.includes(q)) rank = 2;
        if (rank >= 0) ranked.push({ entry, rank });
    });
    ranked.sort((a, b) => (
        a.rank - b.rank
        || TYPE_ORDER[a.entry.type] - TYPE_ORDER[b.entry.type]
        || a.entry.name.length - b.entry.name.length
        || a.entry.name.localeCompare(b.entry.name, 'sv')
    ));
    return ranked.slice(0, limit).map(({ entry }) => entry);
};
